"use client";

import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Radio } from "antd";
import { useTranslations } from "next-intl";
import { productTranslationName } from "@/lib/utils/util";

interface ProductConfigPanelProps {
	product: any;
	onAttributeChange?: (attributeName: string, value: string) => void;
	selectedAttributes?: Record<string, string>;
}

export default function ProductConfigPanel({ 
	product, 
	onAttributeChange,
	selectedAttributes = {}
}: ProductConfigPanelProps) {
	const t = useTranslations();
	const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
	const isInitialized = useRef(false);

	// 从 product.attributes 和 variants 中提取所有唯一的属性组合
	const attributeGroups = useMemo(() => {
		// 首先尝试从 product.attributes 获取属性定义
		// 然后从 variants 中收集每个属性的所有可能值
		if (!product?.variants || product.variants.length === 0) {
			return [];
		}

		const attributeMap = new Map<string, Set<string>>();

		// 方法1: 从 product.attributes 获取属性定义（如果存在）
		if (product.attributes && Array.isArray(product.attributes)) {
			product.attributes.forEach((attr: any) => {
				const attrName = attr.attribute?.translation?.name || attr.attribute?.name || "";
				if (attrName && attr.values && Array.isArray(attr.values)) {
					if (!attributeMap.has(attrName)) {
						attributeMap.set(attrName, new Set());
					}
					attr.values.forEach((value: any) => {
						const valueName = value.translation?.name || value.name || "";
						if (valueName) {
							attributeMap.get(attrName)!.add(valueName);
						}
					});
				}
			});
		}

		// 方法2: 如果方法1没有结果，从 variants 中提取
		if (attributeMap.size === 0) {
			product.variants.forEach((variant: any) => {
				if (variant.attributes && Array.isArray(variant.attributes)) {
					variant.attributes.forEach((attr: any) => {
						const attrName = attr.attribute?.translation?.name || attr.attribute?.name || "";
						const attrValue = attr.values?.[0]?.translation?.name || attr.values?.[0]?.name || "";
						
						if (attrName && attrValue) {
							if (!attributeMap.has(attrName)) {
								attributeMap.set(attrName, new Set());
							}
							attributeMap.get(attrName)!.add(attrValue);
						}
					});
				}
			});
		}

		// 转换为数组格式
		return Array.from(attributeMap.entries()).map(([name, values]) => ({
			name,
			values: Array.from(values).sort(),
		}));
	}, [product?.variants, product?.attributes]);

	// 只在首次加载时初始化所有面板为展开状态，使用延迟展开以显示过渡效果
	useEffect(() => {
		if (!isInitialized.current && attributeGroups.length > 0) {
			// 先确保初始状态为折叠
			setExpandedPanels([]);

			// 一次性展开，避免多个定时器导致卡顿
			const timer = setTimeout(() => {
				setExpandedPanels(attributeGroups.map((_, index) => index.toString()));
			}, 120);

			isInitialized.current = true;

			return () => {
				clearTimeout(timer);
			};
		}
	}, [attributeGroups.length]);

	const togglePanel = useCallback((panelKey: string) => {
		setExpandedPanels((prev) =>
			prev.includes(panelKey)
				? prev.filter((key) => key !== panelKey)
				: [...prev, panelKey]
		);
	}, []);

	const handleValueChange = (attributeName: string, value: string) => {
		if (onAttributeChange) {
			onAttributeChange(attributeName, value);
		}
	};

	return (
		<div className="overflow-hidden border border-gray-200 bg-white shadow-md w-full">
			{/* 标题 */}
			<div className="bg-mainColor px-6 py-4">
				<span className="text-xl font-bold text-white flex items-center gap-2">
					{t("product.PleaseConfigureParameters")}
				</span>
			</div>

			{/* 配置项 */}
			<div className="list-cate">
				{attributeGroups.length > 0 ? (
					attributeGroups.map((group, index) => {
						const panelKey = index.toString();
						const isExpanded = expandedPanels.includes(panelKey);
						
						return (
							<div key={panelKey} className="relative">
								<div
									className={`group flex items-center justify-between cursor-pointer py-3 px-5 transition-all duration-200 hover:bg-mainColor/20 bg-gray-100`}
								>
									<span className="text-base font-medium text-gray-800 flex-1">
										{group.name}
									</span>
									<button
										onClick={(e) => {
											e.preventDefault();
											togglePanel(panelKey);
										}}
										className="flex h-7 w-7 items-center justify-center text-gray-600 hover:text-gray-800 transition-colors ml-3 text-xl font-bold"
									>
										{isExpanded ? "-" : "+"}
									</button>
								</div>

								<div
									className={`overflow-hidden transition-all ease-in-out
										${isExpanded ? "max-h-[5000px] opacity-100" : "max-h-0 opacity-0"}`}
									style={{
										transitionProperty: 'max-height, opacity',
										transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
										transitionDuration: '800ms', // 更慢的过渡效果
									}}
								>
									<div className="bg-white pt-2 pb-2 px-5 product-config-radio-group">
										<Radio.Group
											value={selectedAttributes[group.name] || null}
											onChange={(e) => handleValueChange(group.name, e.target.value)}
										>
											<div className="flex flex-col gap-3">
												{group.values.map((value, valueIndex) => (
													<Radio key={valueIndex} value={value} className="!text-base !text-gray-700 hover:text-mainColor">
														{value}
													</Radio>
												))}
											</div>
										</Radio.Group>
									</div>
								</div>
							</div>
						);
					})
				) : (
					<div className="text-gray-500 text-center py-8">
						{t("product.NoAttributesAvailable")}
					</div>
				)}
			</div>
		</div>
	);
}

